import { useState } from "react";
import { Search, Plus, Trophy, Target, Clock, TrendingUp, Pencil, Trash2 } from "lucide-react";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import { Select, Modal, Form, Input, InputNumber, DatePicker, Button, Tag, Popconfirm, Table, message, Tooltip } from "antd";
import dayjs from "dayjs";
import { useTenderList, useTenderCreate, useTenderUpdate, useTenderDelete, ITender, TenderStatus } from "../../../hooks/api/tenders/hooks";
import { useClientList } from "../../../hooks/api/clients/hooks";
import { IClient } from "../../../types/api/client";

// Must match the `tenders.status` enum in the DB exactly — see the note in
// hooks/api/tenders/hooks.ts. "preparing" / "under_review" / "no_bid" are
// not valid values in the live table and crashed tender save.
const STATUS_CFG: Record<TenderStatus, { cls: string; label: string; color: string }> = {
  tracking:     { cls: "bg-slate-50 text-slate-500 border-slate-200", label: "Tracking", color: "default" },
  submitted:    { cls: "bg-blue-50 text-blue-700 border-blue-200", label: "Submitted", color: "blue" },
  won:          { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Won", color: "green" },
  lost:         { cls: "bg-red-50 text-red-600 border-red-200", label: "Lost", color: "red" },
  withdrawn:    { cls: "bg-amber-50 text-amber-600 border-amber-200", label: "Withdrawn", color: "orange" },
};

const STATUS_OPTS = Object.entries(STATUS_CFG).map(([v, c]) => ({ value: v, label: c.label }));

function fmt(n: number | null | undefined) {
  if (n == null) return "—";
  return `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function TenderModal({ open, editing, onClose, onSuccess }: {
  open: boolean; editing?: ITender | null; onClose: () => void; onSuccess: () => void;
}) {
  const [form] = Form.useForm();
  const [messageApi, ctx] = message.useMessage();
  const isEdit = !!editing;

  const { data: clientRes } = useClientList({ page: 1, limit: 200 });
  const clients: IClient[] = (clientRes?.data as IClient[] | undefined) ?? [];
  const clientOptions = clients.map((c) => ({ value: c.id, label: c.business_name || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() }));

  const { mutate: create, isPending: creating } = useTenderCreate({
    onSuccess: () => { messageApi.success("Tender created."); form.resetFields(); onSuccess(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });
  const { mutate: update, isPending: updating } = useTenderUpdate({
    onSuccess: () => { messageApi.success("Tender updated."); onSuccess(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  const handleOpen = (v: boolean) => {
    if (!v) return;
    if (editing) {
      form.setFieldsValue({
        title: editing.title,
        reference: editing.reference,
        status: editing.status,
        value_estimate: editing.value_estimate,
        client_id: editing.client_id,
        submission_deadline: editing.submission_deadline ? dayjs(editing.submission_deadline) : null,
        outcome_date: editing.outcome_date ? dayjs(editing.outcome_date) : null,
        document_url: editing.document_url,
        notes: editing.notes,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ status: "tracking" });
    }
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const payload = {
        ...values,
        submission_deadline: values.submission_deadline ? values.submission_deadline.format("YYYY-MM-DD") : null,
        outcome_date: values.outcome_date ? values.outcome_date.format("YYYY-MM-DD") : null,
      };
      if (isEdit && editing) { update({ ...payload, id: editing.id }); } else { create(payload); }
    });
  };

  return (
    <Modal open={open} onCancel={onClose} afterOpenChange={handleOpen} title={isEdit ? `Edit — ${editing?.reference ?? editing?.title}` : "New Tender"} width={600} footer={null} destroyOnClose>
      {ctx}
      <Form form={form} layout="vertical" size="small" className="mt-2">
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="title" label="Tender Title" rules={[{ required: true }]}>
            <Input placeholder="e.g. Annual Fire Safety Inspection Contract" />
          </Form.Item>
          <Form.Item name="reference" label="Reference">
            <Input placeholder="e.g. TND-0042" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="status" label="Status">
            <Select options={STATUS_OPTS} />
          </Form.Item>
          <Form.Item name="value_estimate" label="Estimated Value (£)">
            <InputNumber min={0} prefix="£" className="w-full" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="submission_deadline" label="Submission Deadline">
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="outcome_date" label="Outcome Date">
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
        </div>
        <Form.Item name="client_id" label="Client / Authority">
          <Select showSearch allowClear placeholder="Select client…" options={clientOptions}
            filterOption={(input, opt) => ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())} />
        </Form.Item>
        <Form.Item name="document_url" label="Document URL">
          <Input placeholder="https://…" />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
        <div className="flex justify-end gap-2 mt-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={creating || updating} onClick={handleSubmit}>
            {isEdit ? "Save" : "Create Tender"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

const TendersPage = () => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ITender | null>(null);
  const [messageApi, ctx] = message.useMessage();

  const queryParams = { page, limit: 20, ...(search ? { search } : {}), ...(statusFilter ? { status: statusFilter } : {}) };
  const { data: listData, isLoading, refetchWithQuery } = useTenderList(queryParams);
  const tenders: ITender[] = listData?.data ?? [];
  const extra = (listData as any)?.extra ?? {};
  const total = Number(extra.total ?? 0);
  const stats = extra.stats ?? {};

  const { mutate: deleteTender } = useTenderDelete({
    onSuccess: () => { messageApi.success("Tender deleted."); refetchWithQuery({ page }); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  const columns = [
    { title: "Ref", dataIndex: "reference", key: "reference", render: (v: string | null) => <span className="font-mono text-xs text-primary font-semibold">{v || "—"}</span> },
    { title: "Title", dataIndex: "title", key: "title", render: (v: string) => <span className="text-sm text-slate-800 font-medium">{v}</span> },
    { title: "Client", dataIndex: "client_name", key: "client_name", render: (v: string | null) => <span className="text-sm text-slate-600">{v || "—"}</span> },
    { title: "Value", dataIndex: "value_estimate", key: "value_estimate", align: "right" as const, render: (v: number | null) => <span className="text-sm font-semibold tabular-nums">{fmt(v)}</span> },
    { title: "Deadline", dataIndex: "submission_deadline", key: "submission_deadline", render: (v: string | null) => <span className="text-xs text-slate-500 whitespace-nowrap">{v ? dayjs(v).format("DD MMM YYYY") : "—"}</span> },
    { title: "Status", dataIndex: "status", key: "status", render: (v: TenderStatus) => <Tag color={STATUS_CFG[v]?.color ?? "default"}>{STATUS_CFG[v]?.label ?? v}</Tag> },
    {
      title: "Actions", key: "actions",
      render: (_: any, row: ITender) => (
        <div className="flex items-center gap-1">
          <Tooltip title="Edit"><Button size="small" icon={<Pencil size={11} />} onClick={() => { setEditing(row); setModalOpen(true); }} /></Tooltip>
          <Popconfirm title="Delete this tender?" onConfirm={() => deleteTender(row.id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Tooltip title="Delete"><Button size="small" icon={<Trash2 size={11} />} danger type="text" /></Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-slate-50/50">
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Tenders</h1>
          <p className="text-slate-400 text-sm">Bid management, win/loss tracking and pipeline</p>
        </div>
        <ButtonWrapper type="primary" icon={<Plus size={15} />} onClick={() => { setEditing(null); setModalOpen(true); }} className="flex items-center gap-1.5">
          New Tender
        </ButtonWrapper>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center"><Target size={18} className="text-blue-500" /></div>
          <div><div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium">Active Bids</div><div className="text-2xl font-bold text-slate-800">{Number(stats.active ?? 0)}</div></div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center"><TrendingUp size={18} className="text-indigo-500" /></div>
          <div><div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium">Active Pipeline</div><div className="text-2xl font-bold text-slate-800">{fmt(Number(stats.pipeline ?? 0))}</div></div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center"><Trophy size={18} className="text-emerald-500" /></div>
          <div><div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium">Won</div><div className="text-2xl font-bold text-emerald-600">{Number(stats.won ?? 0)}</div></div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center"><Clock size={18} className="text-amber-500" /></div>
          <div>
            <div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium">Win Rate</div>
            <div className="text-2xl font-bold text-slate-800">
              {(Number(stats.won ?? 0) + Number(stats.lost ?? 0)) > 0
                ? `${Math.round((Number(stats.won ?? 0) / (Number(stats.won ?? 0) + Number(stats.lost ?? 0))) * 100)}%`
                : "—"}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="text-slate-400 absolute left-3 top-2.5 z-10" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); refetchWithQuery({ search: e.target.value, page: 1 }); setPage(1); }}
              placeholder="Search tenders…"
              className="pl-9 pr-4 h-10 text-sm border border-slate-200 rounded-lg w-full bg-white focus:outline-none focus:border-primary"
            />
          </div>
          <Select allowClear placeholder="Filter by status" style={{ width: 180 }} value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); refetchWithQuery({ status: v, page: 1 }); }}
            options={STATUS_OPTS} />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          dataSource={tenders}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ current: page, pageSize: 20, total, showSizeChanger: false, showTotal: (t) => `${t} tenders`, onChange: (p) => { setPage(p); refetchWithQuery({ page: p }); } }}
          scroll={{ x: 800 }}
        />
      </div>

      <TenderModal open={modalOpen} editing={editing} onClose={() => { setModalOpen(false); setEditing(null); }} onSuccess={() => { setModalOpen(false); setEditing(null); refetchWithQuery({ page }); }} />
    </div>
  );
};

export default TendersPage;
